/**
 * A cell component showing one usage value.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2026
 */

import { CompanyConsumption, Util } from '@amplify3/duplo-back-end'
import { ReactNode } from 'react'
import './UsageCell.css'
import QuotaProgressBar from './UsageQuotaProgressBar'

interface UsageCellContainerProps {
  consumption?: CompanyConsumption
  contract_start_date?: string
}

const UsageCellContainer = (props: UsageCellContainerProps) => {
  const is_quota_alert = (props.consumption?.quota_percent_per_year ?? 0) > 80

  function formatDate(strDate: string | undefined) {
    if (!strDate) {
      return ''
    }

    const date = new Date(strDate)
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }

    const formattedDate: string = date.toLocaleDateString(
      Util.getLocale(),
      options
    )

    return formattedDate
  }

  return (
    <div className="usage-cell-container">
      <UsageCell
        id="contract-period"
        title="Contract period"
        value={formatDate(props.consumption?.contract_start_date)}
        detail={`reconducted: ${formatDate(
          props.consumption?.next_contract_date
        )}`}
      />

      <UsageCell
        id="total-scranned-tracks"
        title="Total scanned tracks"
        value={`${props.consumption?.total_used_files ?? 0}`}
        detail="since account creation"
      />

      <UsageCell
        id="this-year-tracks"
        title="This year's tracks"
        value={`${props.consumption?.current_year_used_files ?? 0}`}
        detail="current contract period"
      />

      <UsageCell
        id="annual-limit"
        title="Annual limit"
        value={`${props.consumption?.limit ?? 0}`}
        is_alert={is_quota_alert}
        detail={
          <QuotaProgressBar
            is_alert={is_quota_alert}
            percent={props.consumption?.quota_percent_per_year ?? 0}
          />
        }
      />
    </div>
  )
}

interface UsageCellProps {
  id?: string
  title: string
  value: string
  detail?: ReactNode | string
  is_alert?: boolean
}

const UsageCell = (props: UsageCellProps) => {
  const className = props.id ? `usage-cell ${props.id}` : 'usage-cell'

  return (
    <div className={className}>
      <div className={`highlight-border${props.is_alert ? ' alert' : ''}`} />
      <div className="container">
        <div className="title">{props.title}</div>
        <div className="value">{props.value}</div>
        {props.detail && <div className="detail">{props.detail}</div>}
      </div>
    </div>
  )
}

export default UsageCellContainer
